/**
 * Shared data-age utility.
 * Pure computation — no React hooks required (see useDataAge below for the
 * live-updating variant). Safe to call inside any component.
 */

import { useEffect, useState } from 'react';

const WARN_MIN  = 10;
const STALE_MIN = 30;

export interface DataAge {
  ageMin:  number | null;
  stale:   boolean;          // true when ageMin > STALE_MIN
  tier:    'fresh' | 'warn' | 'stale' | 'unknown';
  label:   string;           // "sprzed X min" | "przed chwilą" | "—"
  fullUtc: string;           // tooltip: "2026-07-06 10:36:04.000 UTC"
}

const HAS_TZ = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * Some backend timestamps arrive without a timezone marker (naive-UTC
 * strings). Treat any marker-less ISO string as UTC instead of letting the
 * browser assume local time — this was the root cause of the +120min stale
 * bug in CEST (UTC+2). Idempotent: a string that already carries Z/+00:00
 * passes through unchanged, so this is safe regardless of backend/frontend
 * deploy order.
 */
export function parseUtc(iso: string): Date {
  return new Date(HAS_TZ.test(iso) ? iso : `${iso}Z`);
}

export function dataAge(iso: string | null | undefined): DataAge {
  if (!iso) return { ageMin: null, stale: false, tier: 'unknown', label: '—', fullUtc: '—' };
  try {
    const ts     = parseUtc(iso);
    // floor, not round: "N min ago" means N full minutes have elapsed —
    // a 30s-old record must read "przed chwilą", not round up to "1 min".
    const ageMin = Math.floor((Date.now() - ts.getTime()) / 60_000);
    const stale  = ageMin > STALE_MIN;
    const tier: DataAge['tier'] = ageMin > STALE_MIN ? 'stale' : ageMin > WARN_MIN ? 'warn' : 'fresh';
    const label  = ageMin < 1   ? 'przed chwilą'
                 : ageMin === 1 ? 'sprzed 1 min'
                 : `sprzed ${ageMin} min`;
    const fullUtc = ts.toISOString().replace('T', ' ').replace('Z', ' UTC');
    return { ageMin, stale, tier, label, fullUtc };
  } catch {
    return { ageMin: null, stale: false, tier: 'unknown', label: '—', fullUtc: '—' };
  }
}

/**
 * Same as dataAge(), but re-renders on an interval so the label/tier advance
 * on their own (e.g. "przed chwilą" -> "sprzed 1 min") without new data.
 */
export function useDataAge(iso: string | null | undefined, intervalMs = 15_000): DataAge {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return dataAge(iso);
}
